import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import InputOtp10 from '@/components/ui/input-otp-10';
import {
  getMfaTrustVersion,
  saveMfaDeviceTrust,
} from '@/utils/mfaDeviceTrust';
import type { User } from '@supabase/supabase-js';

type MfaEnrollFormProps = {
  user: User;
  onEnrolled: () => void;
  /** True while the screen slides away after enrollment succeeds. */
  leaving?: boolean;
};

export function MfaEnrollForm({ user, onEnrolled, leaving = false }: MfaEnrollFormProps) {
  const [factorId, setFactorId] = useState<string | null>(null);
  const [qr, setQr] = useState<string>('');
  const [secret, setSecret] = useState<string>('');
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [setupError, setSetupError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(true);
  const [loadingSetup, setLoadingSetup] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingSetup(true);
      setSetupError(null);
      try {
        const { data, error: enrollError } = await supabase.auth.mfa.enroll({
          factorType: 'totp',
          friendlyName: 'Authenticator app',
        });
        if (enrollError) throw enrollError;
        if (cancelled) return;
        setFactorId(data.id);
        setQr(data.totp.qr_code);
        setSecret(data.totp.secret);
      } catch (e) {
        if (!cancelled) {
          setSetupError((e as Error)?.message || 'Could not start authenticator setup.');
        }
      } finally {
        if (!cancelled) setLoadingSetup(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleComplete = async (code: string) => {
    if (!factorId) return;
    setError(null);
    setIsVerifying(true);
    try {
      const challenge = await supabase.auth.mfa.challenge({ factorId });
      if (challenge.error) throw challenge.error;

      const verify = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challenge.data.id,
        code,
      });
      if (verify.error) throw verify.error;

      if (rememberDevice) {
        saveMfaDeviceTrust(user.id, getMfaTrustVersion(user));
      }
      setIsSuccess(true);
      onEnrolled();
    } catch (e) {
      setError((e as Error)?.message || 'Could not verify authenticator code.');
      setValue('');
    } finally {
      setIsVerifying(false);
    }
  };

  if (loadingSetup) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50 dark:bg-slate-950 text-sm text-slate-600">
        Preparing authenticator setup…
      </div>
    );
  }

  if (setupError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50 dark:bg-slate-950">
        <div className="max-w-md rounded-xl border border-red-200 bg-white p-6 text-sm text-red-700">
          {setupError}
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex flex-col items-center justify-center gap-6 p-6 bg-slate-100 dark:bg-slate-950 ${leaving ? 'animate-slide-out-right' : ''}`}>
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wide dark:text-white">
          Set up authenticator
        </h2>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          Scan this QR code with Google Authenticator, Authy, or a similar app. Then enter the 6-digit code below.
        </p>
        {qr ? (
          <div className="mt-4 flex justify-center">
            <img src={qr} alt="Authenticator QR code" className="h-44 w-44 rounded-lg border border-slate-100 bg-white" />
          </div>
        ) : null}
        {secret ? (
          <p className="mt-3 break-all text-center text-[11px] font-mono text-slate-500">
            Manual key: {secret}
          </p>
        ) : null}
      </div>
      <InputOtp10
        value={value}
        onChange={setValue}
        onComplete={(code) => void handleComplete(code)}
        isVerifying={isVerifying}
        isSuccess={isSuccess}
        error={error}
        title="Confirm setup"
        description="Enter the 6-digit code shown in your authenticator app."
        successTitle="Authenticator enabled"
        successDescription="MFA is set up. Loading your workspace…"
        rememberDevice={rememberDevice}
        onRememberDeviceChange={setRememberDevice}
        showRememberDevice
      />
    </div>
  );
}
